import React from 'react';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type MetricItem = {
    label: string;
    value: string | number;
    color?: string;
    icon?: string;
    tooltip?: string;
};

type ModernMultiValueWidgetProps = {
    className?: string;
    title: string;
    icon?: string;
    iconColor?: string;
    metrics: MetricItem[];
    chartSeries?: any[];
    chartColor?: string;
    chartLabel?: string;
    chartSubtext?: string;
    onClick?: () => void;
};

export const ModernMultiValueWidget: React.FC<ModernMultiValueWidgetProps> = ({
    className,
    title,
    icon,
    iconColor = '#009ef7',
    metrics,
    onClick
}) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
    const [hoveredMetric, setHoveredMetric] = React.useState<number | null>(null);

    return (
        <div
            className={`card h-100 ${className || ''} ${onClick ? 'cursor-pointer hover-elevate-up' : ''}`}
            onClick={onClick}
            style={{
                backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            {/* Decorative background gradient */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '200px',
                    height: '200px',
                    background: `radial-gradient(circle at top right, ${iconColor}15, transparent 70%)`,
                    pointerEvents: 'none'
                }}
            />

            <div className="card-body p-4 position-relative">
                {/* Header */}
                <div className="d-flex align-items-center mb-4">
                    {icon && (
                        <div
                            className="symbol symbol-40px me-2"
                            style={{
                                backgroundColor: `${iconColor}20`,
                                border: `2px solid ${iconColor}40`
                            }}
                        >
                            <div className="symbol-label">
                                <i className={`bi ${icon} fs-2`} style={{color: iconColor}}></i>
                            </div>
                        </div>
                    )}
                    <div>
                        <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'} fw-bolder fs-5 d-block`}>
                            {title}
                        </span>
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} fw-semibold fs-8`}>
                            Live metrics
                        </span>
                    </div>
                </div>

                {/* Content Area: Metrics */}
                <div className="row g-0">
                    {/* Metrics Column */}
                    <div className="col-12">
                        <div className="row g-3">
                            {metrics.map((metric, index) => (
                                <div key={index} className="col-6">
                                    <div className="position-relative" style={{paddingLeft: '10px'}}>
                                        {/* Accent bar on the left */}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '3px',
                                                bottom: '3px',
                                                width: '3px',
                                                borderRadius: '3px',
                                                background: `linear-gradient(180deg, ${iconColor}, ${iconColor}80)`,
                                                boxShadow: `0 0 8px ${iconColor}40`
                                            }}
                                        />
                                        {/* Metric content */}
                                        <div className="d-flex flex-column">
                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                <div className="d-flex align-items-center gap-1">
                                                    <span
                                                        className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} fw-semibold fs-9 text-uppercase`}
                                                    >
                                                        {metric.label}
                                                    </span>
                                            {metric.tooltip && (
                                                <div
                                                    className="position-relative"
                                                    onMouseEnter={() => setHoveredMetric(index)}
                                                    onMouseLeave={() => setHoveredMetric(null)}
                                                    style={{cursor: 'help'}}
                                                >
                                                    <div
                                                        style={{
                                                            width: '14px',
                                                            height: '14px',
                                                            borderRadius: '50%',
                                                            backgroundColor: `${iconColor}20`,
                                                            border: `1.5px solid ${iconColor}60`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '0.6rem',
                                                            fontWeight: 'bold',
                                                            color: iconColor,
                                                            transition: 'all 0.2s ease',
                                                            transform: hoveredMetric === index ? 'scale(1.15)' : 'scale(1)',
                                                            boxShadow: hoveredMetric === index ? `0 0 8px ${iconColor}40` : 'none'
                                                        }}
                                                    >
                                                        i
                                                    </div>
                                                    {/* Tooltip popup */}
                                                    {hoveredMetric === index && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                bottom: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%) translateY(-8px)',
                                                                backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
                                                                color: isDarkMode ? '#ffffff' : '#1e1e2d',
                                                                padding: '8px 12px',
                                                                borderRadius: '8px',
                                                                boxShadow: `0 4px 20px ${isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`,
                                                                fontSize: '0.75rem',
                                                                fontWeight: '500',
                                                                whiteSpace: 'nowrap',
                                                                zIndex: 1000,
                                                                border: `1px solid ${isDarkMode ? '#2b2b40' : '#e4e6ef'}`,
                                                                animation: 'fadeIn 0.2s ease-in-out'
                                                            }}
                                                        >
                                                            {metric.tooltip}
                                                            {/* Arrow */}
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '6px solid transparent',
                                                                    borderRight: '6px solid transparent',
                                                                    borderTop: `6px solid ${isDarkMode ? '#1e1e2d' : '#ffffff'}`
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {metric.icon && (
                                            <i
                                                className={`bi ${metric.icon} fs-7`}
                                                style={{color: iconColor, opacity: 0.4}}
                                            />
                                        )}
                                    </div>
                                    <div className="d-flex align-items-baseline">
                                        <span
                                            className={`fw-bolder ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                            style={{
                                                fontSize: '1.5rem',
                                                lineHeight: '1.2',
                                                letterSpacing: '-0.02em',
                                                ...(metric.color ? {color: metric.color} : {})
                                            }}
                                        >
                                            {metric.value}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                        </div>
                    </div>

                </div>

                {/* Add fadeIn animation */}
                <style>{`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateX(-50%) translateY(-4px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(-50%) translateY(-8px);
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};
